import React from 'react';
import {useNavigate} from 'react-router-dom';
import AlertFormInitializationFragment from '@app/site/secured/alert/fragment/AlertFormInitializationFragment';
import AlertType from '@app/model/alert/AlertType';
import CompanyHotel from '@app/model/company_hotel/CompanyHotel';
import AlertInput from '@app/model/alert/input/AlertInput';
import useToaster from '@app/component/overlays/toaster/ToasterHook';
import {useTranslation} from '@app/component/translation/Translation';
import {useForm} from 'react-hook-form';
import {AlertQueryKeys} from '@app/model/alert/service/AlertQueryKeys';
import AlertService from '@app/model/alert/service/AlertService';
import {useMutation, useQueryClient} from '@tanstack/react-query';
import _isEmpty from 'lodash/isEmpty';
import FormMultiSelect from '@app/component/form/input/select/FormMultiSelect';
import FormSelect from '@app/component/form/input/select/FormSelect';
import FormInputNumber from '@app/component/form/input/text/FormInputNumber';
import {CheckCircleIcon} from '@heroicons/react/20/solid';
import AlertFilterRoomTypesField from '@app/site/secured/alert/fields/AlertFilterRoomTypesField';
import AlertFilterOnlyRefundableField from '@app/site/secured/alert/fields/AlertFilterOnlyRefundableField';
import AlertFilterLengthOfStayField from '@app/site/secured/alert/fields/AlertFilterLengthOfStayField';
import AlertScheduleField from '@app/site/secured/alert/fields/AlertScheduleField';
import AlertTimeFrameField from '@app/site/secured/alert/fields/AlertTimeFrameField';
import FormInputText from '@app/component/form/input/text/FormInputText';
import FormButton from '@app/component/form/button/FormButton';
import _get from 'lodash/get';
import _set from 'lodash/set';
import CompetitorsPriceRateAlertRuleInclusion from '@app/model/alert/CompetitorsPriceRateAlertRuleInclusion';
import CompetitorsPriceRateAlertRuleInput from '@app/model/alert/input/CompetitorsPriceRateAlertRuleInput';
import CompetitorsPriceRateAlertRuleCalculation from '@app/model/alert/CompetitorsPriceRateAlertRuleCalculation';
import CompetitorsPriceRateAlertRule from '@app/model/alert/CompetitorsPriceRateAlertRule';
import CompetitorsPriceRateAlertRuleRoomComparison from '@app/model/alert/CompetitorsPriceRateAlertRuleRoomComparison';
import {useSelectedCompanyHotel} from '@app/site/secured/provider/useSelectedCompanyHotel';


const CompetitorsPriceRateAlertPage: React.FunctionComponent<{
    alertId: number | undefined,
    isCopy?: boolean
}> = ({alertId, isCopy = false}) => {

    const companyHotel = useSelectedCompanyHotel();

    if (!!companyHotel) {

        return <AlertFormInitializationFragment alertId={alertId}
                                                alertType={AlertType.COMPETITORS_PRICE_RATE}
                                                companyHotel={companyHotel}
                                                isCopy={isCopy} renderForm={(companyHotel, defaultValues) =>
            <CustomPriceRateChangeAlert companyHotel={companyHotel} defaultValues={defaultValues}/>}/>;

    }

    return <></>;

};

const CustomPriceRateChangeAlert: React.FunctionComponent<{
    companyHotel: CompanyHotel,
    defaultValues: AlertInput
}> = ({companyHotel, defaultValues}) => {
    const toaster = useToaster();
    const {t} = useTranslation();
    const navigate = useNavigate();
    const form = useForm<AlertInput>({
        defaultValues,
    });

    const alertRule: CompetitorsPriceRateAlertRuleInput = form.watch('competitorsPriceRateAlertRuleInput');

    const queryClient = useQueryClient();
    const saveAlertMutation = useMutation({
        mutationFn: AlertService.saveAlert,
        onSuccess: (data) => {
            toaster.success('Alert', 'Alert saved successfully!');
            queryClient.invalidateQueries({queryKey: AlertQueryKeys.lists()});
            queryClient.setQueryData(AlertQueryKeys.detail(data.alert.id), data);
            navigate('/secured/alerts');
        },
        onError: (error: string) => toaster.error('Alert', t(error)),
    });


    const onSubmit = (input: AlertInput) => {
        if (_isEmpty(input.filter.roomTypes)) {
            form.setError('filter.roomTypes', {message: 'Please select at least one room type!'});
        } else {
            allToEmpty(input.filter, 'roomTypes');
            if (!!input.schedule.weekdays)
                allToEmpty(input.schedule, 'weekdays');

            // @ts-ignore
            input.filter.onlyRefundable = input.filter.onlyRefundable == 'true';

            saveAlertMutation.mutate(input);
        }
    };

    const allToEmpty = (obj: any, arrayProperty: string) => {
        if (_get(obj, arrayProperty).includes('ALL')) _set(obj, arrayProperty, undefined);
    };

    const isEdit = !!defaultValues.id;
    const selectedInclusion = form.watch('competitorsPriceRateAlertRuleInput.competitorsInclusion');

    return <div className="mx-auto max-w-7xl py-6 px-4 sm:px-6 lg:px-8">
        {/* Set Price Alert */}
        <div className="mx-auto mt-8 lg:mx-0 lg:max-w-none bg-white p-4 sm:p-10 rounded-xl">
            <div className="mb-6">
                <h2 className="text-base font-semibold leading-7 text-gray-900">Add New Rate Change Alert</h2>
                <p className="text-sm leading-6 text-gray-500">
                    Configure and personalize your alert in the form provided below.
                </p>
            </div>
            <div className="pt-12 border-t border-gray-200">
                <div className="space-y-12">
                    {/* Section - Rulesets */}
                    <div className="grid grid-cols-1 gap-x-8 gap-y-10 border-b border-gray-900/10 pb-12 md:grid-cols-3">
                        <div>
                            <h2 className="text-base font-semibold leading-7 text-gray-900">Alert Setup</h2>
                            <p className="mt-1 text-sm leading-6 text-gray-600">
                                Start by selecting the competitors you would like to monitor. Then, select the
                                percentage change that
                                will activate the alerts.
                            </p>
                        </div>
                        <div className="grid grid-cols-1 gap-x-6 gap-y-8 sm:grid-cols-6 md:col-span-2">
                            {/* Competitors */}
                            <div className="col-span-full">
                                {!_isEmpty(companyHotel.competitors) &&
                                    <FormMultiSelect name={`competitorsPriceRateAlertRuleInput.competitorIds`}
                                                     form={form} placeholder="Select..."
                                                     label="Select Competitors"
                                                     isRequired
                                                     disabled={isEdit}
                                                     hideErrorMessage
                                                     options={companyHotel.competitors!.map(competitor => {
                                                         return {
                                                             id: 'option_' + competitor.id,
                                                             label: competitor.name,
                                                             value: competitor.id,
                                                         };
                                                     })}/>}
                            </div>

                            <div className="sm:col-span-3">
                                <FormInputNumber name={`competitorsPriceRateAlertRuleInput.priceRatePercentage`}
                                                 label={`Rate Change Percentage`}
                                                 form={form}
                                                 disabled={isEdit}
                                                 min={0}
                                                 max={100}
                                                 isRequired
                                                 placeholder="Insert value"/>
                            </div>
                            <div className="sm:col-span-3">
                                <FormSelect name={`competitorsPriceRateAlertRuleInput.calculation`} label="Trigger"
                                            isRequired form={form}
                                            disabled={isEdit}
                                            size="w-full"
                                            optionsSize="large"
                                            optionsTextSize="sm"
                                            placeholder="Select type" options={[
                                    {
                                        id: 'Increase',
                                        label: 'Increase',
                                        value: CompetitorsPriceRateAlertRuleCalculation.INCREASE,
                                    },
                                    {
                                        id: 'Decrease',
                                        label: 'Decrease',
                                        value: CompetitorsPriceRateAlertRuleCalculation.DECREASE,
                                    },
                                    {
                                        id: 'Increase and Decrease',
                                        label: 'Increase and Decrease',
                                        value: CompetitorsPriceRateAlertRuleCalculation.INCREASE_AND_DECREASE,
                                    },
                                ]}/>
                            </div>

                            <div className="col-span-full">
                                <AlertRuleExplanation form={form}/>
                            </div>
                        </div>
                    </div>
                    <div className="grid grid-cols-1 gap-x-8 gap-y-10 border-b border-gray-900/10 pb-12 md:grid-cols-3">
                        <div>
                            <h2 className="text-base font-semibold leading-7 text-gray-900">Trigger Details</h2><p
                            className="mt-1 text-sm leading-6 text-gray-600">
                            First, select if all or at least one competitor should trigger the alert. Then, choose
                            whether to monitor
                            changes in the lowest rate or a specific room type rate.
                        </p>
                        </div>
                        <div className="grid grid-cols-1 grid-rows-2 gap-x-6 gap-y-8 sm:grid-cols-6 md:col-span-2">
                            <div className="sm:col-span-3">
                                <FormSelect name={`competitorsPriceRateAlertRuleInput.competitorsInclusion`}
                                            label="Monitor Competitor(s) Price Change"
                                            isRequired form={form}
                                            disabled={isEdit}
                                            size="w-full"
                                            optionsSize="xxlarge"
                                            optionsTextSize="sm"
                                            placeholder="Select type" options={[
                                    {
                                        id: 'ALL',
                                        label: 'All competitors should change',
                                        value: CompetitorsPriceRateAlertRuleInclusion.ALL,
                                    },
                                    {
                                        id: 'ANY',
                                        label: 'At least one should change',
                                        value: CompetitorsPriceRateAlertRuleInclusion.ANY,
                                    },
                                    {
                                        id: 'SOME',
                                        label: 'More than selected percentage should change',
                                        value: CompetitorsPriceRateAlertRuleInclusion.SOME
                                    }
                                ]}/>
                            </div>

                            {selectedInclusion === CompetitorsPriceRateAlertRuleInclusion.SOME && (
                                <div className="sm:col-span-3">
                                    <FormInputNumber name={`competitorsPriceRateAlertRuleInput.inclusionPercentage`}
                                                     label={`Monitor Competitor(s) Inclusion Percentage (%)`}
                                                     form={form}
                                                     disabled={isEdit}
                                                     min={0}
                                                     max={100}
                                                     isRequired
                                                     placeholder="Insert value"/>
                                </div>
                            )}

                            <div className="sm:col-span-3 sm:row-start-2">
                                <FormSelect name={`competitorsPriceRateAlertRuleInput.roomComparison`}
                                            label="Monitor Room Type Change"
                                            isRequired form={form}
                                            disabled={isEdit}
                                            size="w-full"
                                            optionsSize="xlarge"
                                            optionsTextSize="sm"
                                            placeholder="Select type" options={[
                                    {
                                        id: 'ALL',
                                        label: 'Lowest Available Rate',
                                        value: CompetitorsPriceRateAlertRuleRoomComparison.ANY,
                                    },
                                    {
                                        id: 'ANY',
                                        label: 'Same Room Type',
                                        value: CompetitorsPriceRateAlertRuleRoomComparison.SAME,
                                    },
                                ]}/>
                            </div>
                        </div>
                    </div>
                    {/* Section - Details */}
                    <div className="grid grid-cols-1 gap-x-8 gap-y-10 border-b border-gray-900/10 pb-12 md:grid-cols-3">
                        <div>
                            <h2 className="text-base font-semibold leading-7 text-gray-900">Alert Details</h2>
                            <p className="mt-1 text-sm leading-6 text-gray-600">
                                Please configure the different details for your alert.
                            </p>
                        </div>

                        <div className="grid grid-cols-1 gap-x-6 gap-y-8 sm:grid-cols-6 md:col-span-2">
                            {/* Room Types */}
                            <div className="col-span-full">
                                <AlertFilterRoomTypesField form={form} companyHotel={companyHotel}/>
                            </div>
                            {/* Rate */}
                            <div className="col-span-full">
                                <AlertFilterOnlyRefundableField form={form}/>
                            </div>
                            {/* Length of stay */}
                            <div className="col-span-full">
                                <AlertFilterLengthOfStayField form={form}/>
                            </div>
                        </div>
                    </div>
                    {/* Section - Timeframes */}
                    <div className="grid grid-cols-1 gap-x-8 gap-y-10 border-b border-gray-900/10 pb-12 md:grid-cols-3">
                        <div>
                            <h2 className="text-base font-semibold leading-7 text-gray-900">Timeframe & Frequency</h2>
                            <p className="mt-1 text-sm leading-6 text-gray-600">
                                Set how often the alert runs and the time period it monitors.
                            </p>
                        </div>

                        <div className="grid grid-cols-1 gap-x-6 gap-y-8 sm:grid-cols-6 md:col-span-2">
                            {/* Frequency */}
                            <div className="col-span-full">
                                <AlertScheduleField form={form} defaultValues={defaultValues}/>
                            </div>
                            <div className="col-span-full">
                                <AlertTimeFrameField form={form}/>
                            </div>
                        </div>
                    </div>
                    {/* Section - Push Notifications */}
                    <div className="grid grid-cols-1 gap-x-8 gap-y-10 border-b border-gray-900/10 pb-12 md:grid-cols-3">
                        <div>
                            <h2 className="text-base font-semibold leading-7 text-gray-900">Finalize Alert</h2>
                            <p className="mt-1 text-sm leading-6 text-gray-600">Assign a unique name to your alert. This
                                will help you easily identify it
                                later.</p>
                        </div>
                        <div className="grid grid-cols-1 gap-x-6 gap-y-8 sm:grid-cols-6 md:col-span-2">
                            {/* Push Notifications */}
                            {/*<div className="col-span-full">
								<AlertAddExtraEmailsField form={form} />
							</div>*/}
                            {/* Alert Name */}
                            <div className="col-span-full">
                                <FormInputText label="Alert Name" name="name" form={form} maxLength={100}
                                               placeholder="Insert alert name" isRequired/>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <div className="flex justify-end my-6 space-x-4">
                <FormButton state="text" to="/secured/alerts"
                            isLoading={saveAlertMutation.isPending}>Cancel</FormButton>
                <FormButton state="primary"
                            onClick={form.handleSubmit(onSubmit)}
                            isLoading={saveAlertMutation.isPending}>{isEdit ? 'Save' : 'Create'} Alert</FormButton>
            </div>
        </div>
    </div>;

};

const AlertRuleExplanation: React.FunctionComponent<{ form: any }> = ({form}) => {

    const alertRule: CompetitorsPriceRateAlertRule = form.watch('competitorsPriceRateAlertRuleInput');

    const explainCalculation = (calculation: CompetitorsPriceRateAlertRuleCalculation) => {
        switch (calculation) {
            case CompetitorsPriceRateAlertRuleCalculation.INCREASE:
                return 'has been increased';
            case CompetitorsPriceRateAlertRuleCalculation.DECREASE:
                return 'has been decreased';
            case CompetitorsPriceRateAlertRuleCalculation.INCREASE_AND_DECREASE:
                return 'has been changed';
        }
    };

    if (_isEmpty(alertRule.competitorIds)
        || !alertRule.calculation
        || !alertRule.priceRatePercentage) return <></>;

    return <div className="w-full rounded-lg bg-white ring-1 ring-black ring-opacity-20">
        <div className="p-4">
            <div className="flex items-start">
                <div className="flex-shrink-0">
                    <CheckCircleIcon className="h-6 w-6 text-green-400"/>
                </div>
                <div className="ml-3 w-0 flex-1 pt-0.5">
                    <p className="text-sm font-medium text-gray-900">Your alert translates
                        to:</p>
                    <p className="mt-1 text-sm text-gray-500">
                        This alert will be triggered
                        if {alertRule.competitorsInclusion === CompetitorsPriceRateAlertRuleInclusion.ALL ? 'all' : 'any'} of
                        the selected
                        competitors
                        price rate change of the
                        last {alertRule.pastPeriodInDays} days, {explainCalculation(alertRule.calculation)} more
                        than {alertRule.priceRatePercentage}%
                    </p>
                </div>
            </div>
        </div>
    </div>;
};

export default CompetitorsPriceRateAlertPage;